import {
  type ExamType,
  US_EXAM_TYPES,
  type UsExamType,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";

// ─── Types ────────────────────────────────────────────────────────────────────

export type CredStatus = "not_started" | "in_progress" | "credentialed";

/**
 * Thresholds a performer must meet to be credentialed for a given exam type.
 *
 * v1: totalExamsMin and avgScoreMin are enforced.
 * truePositiveMin, trueNegativeMin, maintenancePerYear are defined here for
 * future use but set to 0 (not blocking) until per-type positive/negative
 * worksheet detection is implemented.
 */
export type CredentialingThresholds = {
  /** Minimum submitted exams of this type */
  totalExamsMin: number;
  /** Minimum average QA quality score (1–5) — 0 means not enforced */
  avgScoreMin: number;
  /** Minimum true-positive studies — 0 in v1 (not yet enforced) */
  truePositiveMin: number;
  /** Minimum true-negative studies — 0 in v1 (not yet enforced) */
  trueNegativeMin: number;
  /** Annual maintenance studies to retain credentials — 0 in v1 (not yet enforced) */
  maintenancePerYear: number;
};

// ─── Thresholds ───────────────────────────────────────────────────────────────

const US_CREDENTIALING_THRESHOLDS = Object.fromEntries(
  US_EXAM_TYPES.map((examType) => [
    examType,
    {
      totalExamsMin: 30,
      avgScoreMin: 3.0,
      truePositiveMin: 0,
      trueNegativeMin: 0,
      maintenancePerYear: 0,
    },
  ]),
) as Record<UsExamType, CredentialingThresholds>;

/**
 * Per-exam-type credentialing thresholds for this institution (v1 hardcoded).
 *
 * Structure is intentionally API-ready: replace this constant with a
 * useQuery/fetch call from a QA Admin settings endpoint in a future ticket.
 */
export const CREDENTIALING_THRESHOLDS: Record<
  ExamType,
  CredentialingThresholds
> = {
  efast: {
    totalExamsMin: 50,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  cardiac: {
    totalExamsMin: 40,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  lung: {
    totalExamsMin: 40,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  bladder: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  renal: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  aorta: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  pregnancy: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  msk: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  guided_joint_injection: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  guided_tendon_injection: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  guided_nerve_block: {
    totalExamsMin: 30,
    avgScoreMin: 3.0,
    truePositiveMin: 0,
    trueNegativeMin: 0,
    maintenancePerYear: 0,
  },
  ...US_CREDENTIALING_THRESHOLDS,
};

// ─── Status computation ───────────────────────────────────────────────────────

/**
 * Compute credentialing status for a single exam type given the performer's
 * current aggregates and the configured thresholds.
 *
 * @param examCount   - submitted exams of this type
 * @param avgScore    - average review quality score, or null if no reviews yet
 * @param thresholds  - thresholds for this exam type
 */
export const computeCredStatus = (
  examCount: number,
  avgScore: number | null,
  thresholds: CredentialingThresholds,
): CredStatus => {
  if (examCount === 0) return "not_started";

  const meetsExams = examCount >= thresholds.totalExamsMin;

  // When avgScoreMin === 0 the quality threshold is not enforced
  const meetsScore =
    thresholds.avgScoreMin === 0 ||
    (avgScore !== null && avgScore >= thresholds.avgScoreMin);

  return meetsExams && meetsScore ? "credentialed" : "in_progress";
};
